import { useState, KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEdges } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Save, X, Hash, Tag, CheckCircle2, Zap, Shuffle, GitBranch } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { configureTriggerNode } from '@/services/api';
import { setTransitionWeights } from '@/services/api/transitions';
import RandomWeightsEditor, { type WeightsState } from './RandomWeightsEditor';
import type { FlowNode, TriggerItem } from '@/types/flow';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface TriggerConfigProps {
    node: FlowNode;
    onClose: () => void;
}

export default function TriggerConfig({ node, onClose }: TriggerConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [triggers, setTriggers] = useState<TriggerItem[]>(
        (node.data.config?.triggers || []).map((t: any) => ({
            trigger_type: t.trigger_type as TriggerItem['trigger_type'],
            trigger_value: t.trigger_value,
        }))
    );
    const randomObrigatorio = useEdges().filter((e) => e.source === node.id).length > 1;
    const [isRandom, setIsRandom] = useState((node.data.is_random ?? false) || randomObrigatorio);
    const [isSequence, setIsSequence] = useState(node.data.is_sequence ?? false);
    const [newTrigger, setNewTrigger] = useState<TriggerItem>({
        trigger_type: 'keyword',
        trigger_value: '',
    });

    const hasWildcard = triggers.some(t => t.trigger_value === '#');
    const [pesos, setPesos] = useState<WeightsState | null>(null);
    const sorteioLigado = isRandom || randomObrigatorio;
    const pesosInvalidos = sorteioLigado && pesos !== null && !pesos.valid;

    const saveMutation = useMutation({
        mutationFn: async () => {
            await configureTriggerNode(node.data.id, {
                node_name: nodeName,
                triggers: isSequence ? [] : triggers,
                is_random: sorteioLigado,
                is_sequence: isSequence,
            });
            if (sorteioLigado && pesos?.changed && pesos.valid) {
                await setTransitionWeights(node.data.id, pesos.weights);
            }
        },
        onSuccess: async () => {
            toast({ title: 'Gatilho salvo!', description: 'Configurações salvas com sucesso.' });
            await queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleAddTrigger = () => {
        if (!newTrigger.trigger_value.trim()) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Digite um valor.' });
            return;
        }
        setTriggers([...triggers, { ...newTrigger }]);
        setNewTrigger({ ...newTrigger, trigger_value: '' });
    };

    const handleAddWildcard = () => {
        if (hasWildcard) return;
        setTriggers([...triggers, { trigger_type: 'keyword', trigger_value: '#' }]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTrigger();
        }
    };

    const handleRemoveTrigger = (index: number) => {
        setTriggers(triggers.filter((_, i) => i !== index));
    };

    const handleChangeTriggerType = (index: number, newType: TriggerItem['trigger_type']) => {
        setTriggers(triggers.map((t, i) => i === index ? { ...t, trigger_type: newType } : t));
    };

    const getTriggerIcon = (type: string) => {
        switch (type) {
            case 'keyword':
                return <Hash className="w-3.5 h-3.5" />;
            case 'tag':
                return <Tag className="w-3.5 h-3.5" />;
            case 'exact_match':
                return <CheckCircle2 className="w-3.5 h-3.5" />;
            default:
                return <Hash className="w-3.5 h-3.5" />;
        }
    };

    const getTriggerTypeLabel = (type: string) => {
        switch (type) {
            case 'keyword':
                return 'Contém';
            case 'tag':
                return 'Tag';
            case 'exact_match':
                return 'Exato';
            default:
                return type;
        }
    };

    const getTriggerColor = (type: string) => {
        switch (type) {
            case 'keyword':
                return 'bg-cyan-50 border-cyan-200 text-cyan-700';
            case 'tag':
                return 'bg-blue-50 border-blue-200 text-blue-700';
            case 'exact_match':
                return 'bg-green-50 border-green-200 text-green-700';
            default:
                return 'bg-gray-50 border-gray-200 text-gray-700';
        }
    };

    return (
        <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Nome do Gatilho</Label>
                    <Input
                        value={nodeName}
                        onChange={(e) => setNodeName(e.target.value)}
                        placeholder="Ex: Boas-vindas"
                        className="h-10"
                    />
                </div>

                {/* Modo Sequência */}
                <button
                    type="button"
                    onClick={() => setIsSequence(!isSequence)}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${isSequence
                            ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                            : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 text-gray-500 cursor-pointer'
                        }`}
                >
                    <GitBranch className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Modo Sequência</p>
                        <p className="text-xs opacity-70">Entrada apenas via Go To de outro fluxo — não dispara por mensagem</p>
                    </div>
                    {isSequence && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0 text-indigo-500" />}
                </button>

                {/* Random */}
                <button
                    type="button"
                    onClick={() => setIsRandom(!isRandom)}
                    disabled={randomObrigatorio}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${isRandom
                            ? 'border-orange-300 bg-orange-50 text-orange-600'
                            : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50 text-gray-500 cursor-pointer'
                        }
                        ${randomObrigatorio ? 'cursor-not-allowed' : ''}`}
                >
                    <Shuffle className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Próximo node aleatório</p>
                        <p className="text-xs opacity-70">
                            {randomObrigatorio
                                ? 'Obrigatório: este gatilho tem mais de um node conectado'
                                : 'Sorteia um dos nodes conectados a este gatilho'}
                        </p>
                    </div>
                    {isRandom && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0 text-orange-500" />}
                </button>

                {sorteioLigado && <RandomWeightsEditor nodeId={node.id} onChange={setPesos} />}

                {!isSequence && (
                <>
                {/* Wildcard rápido */}
                <button
                    type="button"
                    onClick={handleAddWildcard}
                    disabled={hasWildcard}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${hasWildcard
                            ? 'border-purple-200 bg-purple-50 text-purple-400 cursor-not-allowed'
                            : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50 text-purple-600 cursor-pointer'
                        }`}
                >
                    <Zap className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Ativar com qualquer mensagem (#)</p>
                        <p className="text-xs opacity-70">O fluxo inicia para qualquer texto enviado pelo lead</p>
                    </div>
                    {hasWildcard && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0" />}
                </button>

                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Adicionar Gatilho Específico</Label>

                    <Select
                        value={newTrigger.trigger_type}
                        onValueChange={(value: TriggerItem['trigger_type']) =>
                            setNewTrigger({ ...newTrigger, trigger_type: value })
                        }
                    >
                        <SelectTrigger className="h-10">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="keyword">
                                <div className="flex items-center gap-2">
                                    <Hash className="w-4 h-4" />
                                    <span>Contém a palavra (parcial)</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="exact_match">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Texto exato (prioridade máxima)</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="tag">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4" />
                                    <span>Tag de postback</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <div className="relative">
                        <Input
                            value={newTrigger.trigger_value}
                            onChange={(e) => setNewTrigger({ ...newTrigger, trigger_value: e.target.value })}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                newTrigger.trigger_type === 'tag'
                                    ? 'Digite o ID da tag...'
                                    : 'Digite e pressione Enter...'
                            }
                            className="h-10"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            Enter ↵
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">
                        Gatilhos Configurados
                        {triggers.length > 0 && (
                            <span className="ml-2 text-xs font-normal text-gray-500">({triggers.length})</span>
                        )}
                    </Label>

                    {triggers.length === 0 ? (
                        <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Hash className="w-5 h-5 text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-500">Nenhum gatilho configurado</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Use o botão acima ou digite uma palavra-chave
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {triggers.map((trigger, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 transition-all
                                        ${trigger.trigger_value === '#' ? 'bg-purple-50 border-purple-200 text-purple-700' : getTriggerColor(trigger.trigger_type)}
                                    `}
                                >
                                    {trigger.trigger_value === '#' ? (
                                        <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                                    ) : (
                                        getTriggerIcon(trigger.trigger_type)
                                    )}
                                    <div className="flex-1 min-w-0">
                                        {trigger.trigger_value === '#' ? (
                                            <div className="text-sm font-medium">Qualquer mensagem</div>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const next = trigger.trigger_type === 'keyword' ? 'exact_match' : 'keyword';
                                                        handleChangeTriggerType(index, next);
                                                    }}
                                                    className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 cursor-pointer mb-0.5"
                                                    title="Clique para alternar entre parcial e exato"
                                                >
                                                    {getTriggerTypeLabel(trigger.trigger_type)}
                                                    <span className="text-[10px]">⇄</span>
                                                </button>
                                                <div className="text-sm font-medium leading-none truncate">{trigger.trigger_value}</div>
                                            </>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTrigger(index)}
                                        className="ml-1 hover:bg-black/10 rounded p-0.5 flex-shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {triggers.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-900">
                            <strong>Prioridade:</strong> Texto exato → Contém a palavra → Qualquer mensagem (#).
                        </p>
                    </div>
                )}
                </>
                )}

                {isSequence && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <p className="text-xs text-indigo-900">
                            <strong>Modo Sequência ativo:</strong> este fluxo só será iniciado quando outro fluxo apontar um node Go To para este trigger. Nenhuma palavra-chave é necessária.
                        </p>
                    </div>
                )}
            </div>

            <div className="border-t bg-white p-6 flex-shrink-0">
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || pesosInvalidos}
                    className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
            </div>
        </>
    );
}
